import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

export type UpdateBannerState = 'hidden' | 'update-available' | 'reload-failed';

@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly ETAG_KEY = 'umss_app_etag';
  private readonly RELOAD_KEY = 'umss_reload_requested';
  private readonly POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  private pollingId: ReturnType<typeof setInterval> | null = null;
  private polling = false;

  /**
   * Set in the constructor from sessionStorage before the flag is cleared.
   * Used by notifyChunkError to distinguish a first-time failure
   * from a failure that persists even after a user-triggered reload.
   */
  private readonly didReload: boolean;

  readonly bannerState = signal<UpdateBannerState>('hidden');

  constructor() {
    this.didReload = sessionStorage.getItem(this.RELOAD_KEY) === '1';
    if (this.didReload) {
      sessionStorage.removeItem(this.RELOAD_KEY);
    }
  }

  /**
   * Fetches server's index.html (HEAD, no-store) and compares its ETag /
   * Last-Modified header against the baseline stored in sessionStorage.
   *
   * - First load: stores the tag as baseline → no banner.
   * - Subsequent loads: if tag changed a new bundle is deployed → shows banner.
   *
   * Call once from the authenticated shell layout's ngOnInit, then call
   * startPolling() to continuously monitor for updates.
   */
  async checkForUpdate(): Promise<void> {
    try {
      const res = await fetch('/index.html', {
        method: 'HEAD',
        cache: 'no-store'
      });
      if (!res.ok) return;

      const serverTag =
        res.headers.get('ETag') ?? res.headers.get('Last-Modified');
      if (!serverTag) return;

      const storedTag = sessionStorage.getItem(this.ETAG_KEY);
      if (!storedTag) {
        sessionStorage.setItem(this.ETAG_KEY, serverTag);
      } else if (storedTag !== serverTag) {
        this.bannerState.set('update-available');
      }
    } catch {
      // Network error – fail silently; ChunkLoadErrorHandler handles chunk failures.
    }
  }

  /**
   * Starts periodic polling for updates and also checks on each route navigation.
   * Safe to call multiple times — only the first call sets up subscriptions.
   */
  startPolling(): void {
    if (this.polling) return;
    this.polling = true;

    // Periodic interval check
    this.pollingId = setInterval(
      () => this.checkForUpdate(),
      this.POLL_INTERVAL_MS
    );
    this.destroyRef.onDestroy(() => {
      if (this.pollingId) {
        clearInterval(this.pollingId);
        this.pollingId = null;
      }
    });

    // Check on every route navigation (catches stale chunks faster)
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.checkForUpdate());
  }

  /**
   * Called by ChunkLoadErrorHandler when a webpack chunk 404s.
   * Escalates to 'reload-failed' if the user already tried a reload.
   */
  notifyChunkError(): void {
    this.bannerState.set(this.didReload ? 'reload-failed' : 'update-available');
  }

  /**
   * Clears the stored ETag baseline (so the fresh bundle re-baselines on next
   * load), marks in sessionStorage that a reload was user-requested, then
   * reloads the page.
   */
  reloadToUpdate(): void {
    sessionStorage.removeItem(this.ETAG_KEY);
    sessionStorage.setItem(this.RELOAD_KEY, '1');
    window.location.reload();
  }
}
